<script setup lang="ts">
import SidebarReceiver from './SidebarReceiver.vue';
import Button from 'primevue/button';
import { useToggle } from '@vueuse/core';
import { computed, ref, watchEffect, type Component } from 'vue';

const props = withDefaults(
    defineProps<{
        partial: string | Component;
        fallbackPartial?: string | Component | null;
        partialProps?: Record<string, unknown>;
        sidebarProps?: Record<string, unknown>;
        visible?: boolean;
        position?: 'left' | 'right' | 'top' | 'bottom' | 'full';
        baseZIndex?: number;
        autoZIndex?: boolean;
        dismissable?: boolean;
        showCloseIcon?: boolean;
        modal?: boolean;
        ariaCloseLabel?: string;
        sidebarClass?:
            | string
            | Array<string | Record<string, boolean>>
            | Record<string, boolean>
            | null;
        sidebarStyle?: string | Record<string, string> | null;
    }>(),
    {
        fallbackPartial: null,
        visible: false,
        autoZIndex: true,
        dismissable: true,
        showCloseIcon: true,
        modal: true,
        partialProps: () => ({}),
        sidebarProps: () => ({}),
        position: 'right',
        baseZIndex: undefined,
        ariaCloseLabel: undefined,
        sidebarClass: null,
        sidebarStyle: null,
    }
);

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void;
}>();

const internalVisible = ref<boolean>(false);

watchEffect(() => {
    internalVisible.value = !!props.visible;
});

const visibleModel = computed({
    get: () => internalVisible.value,
    set: (v) => {
        internalVisible.value = v;
        emit('update:visible', v);
    },
});

const toggleVisible = useToggle(visibleModel);
</script>

<template>
    <Button v-bind="$attrs" @click.prevent="toggleVisible()">
        <slot />
    </Button>

    <SidebarReceiver
        v-bind="sidebarProps"
        v-model:visible="visibleModel"
        :partial="partial"
        :fallback-partial="fallbackPartial"
        :partial-props="partialProps"
        :position="position"
        :base-z-index="baseZIndex"
        :auto-z-index="autoZIndex"
        :dismissable="dismissable"
        :show-close-icon="showCloseIcon"
        :modal="modal"
        :class="sidebarClass"
        :style="sidebarStyle"
        :aria-close-label="ariaCloseLabel"
    />
</template>
